import { Form, Input, App, Button, Modal } from 'antd'
import * as Sentry from '@sentry/react'
import { useState } from 'react'
import { supabase } from '../../utils/supabase'
import { useAuthContext } from '../../context/auth'
import {
  FormContainer,
  StyledFormItem,
  FormActions,
  CurrentEmailDisplay,
  EmailValue,
  EmailLabel,
} from './styles/ChangeEmailFormStyles'

export function ChangeEmailForm() {
  const { notification } = App.useApp()
  const { user } = useAuthContext()
  const [form] = Form.useForm()
  const [passwordForm] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [pendingEmail, setPendingEmail] = useState('')

  const handleEmailSubmit = (values: { newEmail: string }) => {
    if (!user?.email) {
      notification.error({
        title: 'Error',
        description: 'Unable to verify current email',
      })
      return
    }

    if (values.newEmail.toLowerCase() === user.email.toLowerCase()) {
      notification.warning({
        title: 'Same Email',
        description: 'The new email is the same as your current email',
      })
      return
    }

    setPendingEmail(values.newEmail)
    setModalOpen(true)
  }

  const handlePasswordConfirm = async () => {
    try {
      await passwordForm.validateFields()
      const password = passwordForm.getFieldValue('password')

      if (!user?.email) return

      setLoading(true)

      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password,
      })

      if (verifyError) {
        notification.error({
          title: 'Incorrect Password',
          description: 'Please enter your correct password',
        })
        setLoading(false)
        return
      }

      const { error: updateError } = await supabase.auth.updateUser({
        email: pendingEmail,
      })

      if (updateError) {
        notification.error({
          title: 'Update Failed',
          description: updateError.message,
        })
      } else {
        notification.success({
          title: 'Confirmation Email Sent',
          description:
            'Check your new email and click the confirmation link to complete the change',
        })
        form.resetFields()
        setModalOpen(false)
        passwordForm.resetFields()
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'settings', action: 'change_email' },
      })
      // Validation failed
    } finally {
      setLoading(false)
    }
  }

  const handleModalClose = () => {
    setModalOpen(false)
    passwordForm.resetFields()
    setPendingEmail('')
  }

  return (
    <FormContainer>
      <CurrentEmailDisplay>
        <EmailLabel>Current email</EmailLabel>
        <EmailValue>{user?.email}</EmailValue>
      </CurrentEmailDisplay>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleEmailSubmit}
        requiredMark={false}
        autoComplete="off"
      >
        <StyledFormItem
          name="newEmail"
          label="New Email Address"
          rules={[
            { required: true, message: 'Please enter your new email address' },
            { type: 'email', message: 'Please enter a valid email address' },
          ]}
        >
          <Input
            placeholder="Enter new email address"
            size="large"
            autoComplete="off"
            data-form-type="other"
          />
        </StyledFormItem>

        <FormActions>
          <Button type="primary" htmlType="submit">
            Change Email
          </Button>
        </FormActions>
      </Form>

      <Modal
        title="Confirm Password"
        open={modalOpen}
        onCancel={handleModalClose}
        footer={[
          <Button key="cancel" onClick={handleModalClose}>
            Cancel
          </Button>,
          <Button key="confirm" type="primary" loading={loading} onClick={handlePasswordConfirm}>
            Confirm
          </Button>,
        ]}
      >
        <p style={{ marginBottom: 16, color: '#666' }}>
          Enter your password to confirm changing your email to <strong>{pendingEmail}</strong>
        </p>
        <Form form={passwordForm} layout="vertical" autoComplete="off">
          <Form.Item
            name="password"
            rules={[{ required: true, message: 'Please enter your password' }]}
          >
            <Input.Password
              placeholder="Enter your password"
              size="large"
              autoComplete="new-password"
              data-form-type="other"
            />
          </Form.Item>
        </Form>
      </Modal>
    </FormContainer>
  )
}
